import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const SDK_SRC = "https://sdk.scdn.co/spotify-player.js";

function loadSdk() {
  if (window.Spotify) return Promise.resolve();
  if (window.__spotifySdkPromise) return window.__spotifySdkPromise;
  window.__spotifySdkPromise = new Promise((resolve) => {
    window.onSpotifyWebPlaybackSDKReady = () => resolve();
    const s = document.createElement("script");
    s.src = SDK_SRC; s.async = true;
    document.body.appendChild(s);
  });
  return window.__spotifySdkPromise;
}

/** Connects the Spotify Web Playback SDK using the company account's token
 *  (served by /spotify/token). Exposes device id, playback state and controls. */
export function useSpotifyPlayer(enabled) {
  const playerRef = useRef(null);
  const [deviceId, setDeviceId] = useState(null);
  const [state, setState] = useState(null);     // SDK WebPlaybackState
  const [volume, setVolumeState] = useState(0.6);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      await loadSdk();
      if (cancelled || playerRef.current) return;
      const player = new window.Spotify.Player({
        name: "Delux Limo Tablet",
        volume: 0.6,
        getOAuthToken: (cb) => {
          api.get("/spotify/token").then(r => cb(r.data.access_token)).catch(() => setError("Spotify token unavailable"));
        },
      });
      player.addListener("ready", async ({ device_id }) => {
        setDeviceId(device_id); setReady(true); setError(null);
        try { await api.put("/spotify/transfer", { device_id }); } catch { /* another device may hold playback — play() passes device_id anyway */ }
      });
      player.addListener("not_ready", () => setReady(false));
      player.addListener("player_state_changed", (s) => setState(s));
      player.addListener("initialization_error", ({ message }) => setError(`Player init failed: ${message}`));
      player.addListener("authentication_error", ({ message }) => setError(`Spotify sign-in problem: ${message}`));
      player.addListener("account_error", () => setError("This Spotify account is not Premium — playback requires Premium."));
      player.addListener("playback_error", ({ message }) => setError(`Playback error: ${message}`));
      playerRef.current = player;
      player.connect();
    })();
    return () => {
      cancelled = true;
      if (playerRef.current) { playerRef.current.disconnect(); playerRef.current = null; }
    };
  }, [enabled]);

  const activate = useCallback(() => {
    // Tablets/phones require a user gesture before audio can start
    try { playerRef.current?.activateElement?.(); } catch { /* noop */ }
  }, []);

  const play = useCallback(async (body) => {
    if (!deviceId) { setError("Player is still starting — try again in a moment"); return false; }
    activate();
    try {
      await api.put("/spotify/play", { device_id: deviceId, ...body });
      setError(null);
      return true;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start playback");
      return false;
    }
  }, [deviceId, activate]);

  const togglePlay = useCallback(() => { activate(); playerRef.current?.togglePlay(); }, [activate]);
  const next = useCallback(() => playerRef.current?.nextTrack(), []);
  const previous = useCallback(() => playerRef.current?.previousTrack(), []);
  const setVolume = useCallback((v) => { setVolumeState(v); playerRef.current?.setVolume(v); }, []);

  return { ready, deviceId, state, error, volume, play, togglePlay, next, previous, setVolume };
}
